import { useRef, useState } from "react";
import { toast } from "sonner";
import { Briefcase, Plus, Upload, Loader2, Scale, FileText, AlertTriangle, Sparkles, Rocket } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { PageHeader, PageState, Empty, Field } from "@/components/app/Common";
import { SimpleSelect, listOptions } from "@/components/app/SimpleSelect";
import { TaskForm, TaskList } from "@/pages/ChiefOfStaff";
import { PlaybookCheck } from "@/components/app/Playbook";
import { ResearchPanel } from "@/components/app/Research";
import { useLoad } from "@/hooks/useLoad";
import { api, errMsg, openFile } from "@/lib/api";
import { fmtDate } from "@/lib/ui";
import { useAuth } from "@/context/AuthContext";

const num = (n, cur = "") => (n == null ? "—" : `${n >= 1e6 ? `${(n / 1e6).toFixed(1)}M` : n >= 1e3 ? `${Math.round(n / 1e3)}K` : n}${cur ? ` ${cur}` : ""}`);
const blank = { name: "", stage: "Sourced", sector: "", location: "", source: "", notes: "", next_action: "", next_action_date: "" };

const DealCard = ({ d, onOpen }) => {
  const m = d.metrics || {};
  const late = d.next_action_date && new Date(`${d.next_action_date.slice(0, 10)}T00:00:00`) < new Date(new Date().toDateString());
  return (
    <button onClick={() => onOpen(d)} className="w-full text-left rounded-xl border border-border bg-card p-3.5 transition-[border-color,transform] duration-200 hover:border-primary/50 hover:-translate-y-0.5" data-testid={`deal-card-${d.id}`}>
      <div className="font-semibold text-sm">{d.name}</div>
      <div className="text-xs text-muted-foreground">{[d.sector, d.location].filter(Boolean).join(" · ") || "—"}</div>
      <div className="mt-2.5 grid grid-cols-3 gap-1 text-[11px]">
        {[["Revenue", num(m.revenue)], ["EBITDA", num(m.ebitda ?? m.sde)], ["Ask", num(m.asking_price)]].map(([l, v]) => (
          <div key={l}><div className="text-muted-foreground">{l}</div><div className="font-mono">{v}</div></div>))}
      </div>
      {m.multiple != null && <div className="mt-1.5 text-[11px] text-muted-foreground">Multiple <span className="font-mono text-foreground">{Number(m.multiple).toFixed(1)}x</span></div>}
      {d.next_action && <div className={`mt-2 text-[11px] ${late ? "text-red-500" : "text-muted-foreground"}`}>Next: {d.next_action}{d.next_action_date && ` · ${fmtDate(d.next_action_date)}`}</div>}
    </button>
  );
};

const Metrics = ({ m }) => (
  <div className="rounded-xl border border-border p-4" data-testid="deal-metrics">
    <div className="flex items-center gap-1.5 text-sm font-semibold mb-2"><Sparkles className="h-4 w-4 text-primary" />Extracted by Claude</div>
    {m.summary && <p className="text-sm mb-3">{m.summary}</p>}
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
      {[["Revenue", num(m.revenue, m.currency)], ["EBITDA", num(m.ebitda, m.currency)], ["SDE", num(m.sde, m.currency)], ["Asking price", num(m.asking_price, m.currency)],
        ["Multiple", m.multiple != null ? `${Number(m.multiple).toFixed(1)}x` : "—"], ["Fiscal year", m.fiscal_year || "—"], ["Employees", m.employees ?? "—"], ["Growth", m.growth || "—"]].map(([l, v]) => (
        <div key={l} className="rounded-md bg-muted/50 p-2"><div className="text-muted-foreground">{l}</div><div className="font-mono mt-0.5 break-words">{v}</div></div>))}
    </div>
    {m.customer_concentration && <p className="text-xs mt-3"><b>Customer concentration:</b> {m.customer_concentration}</p>}
    {m.risks?.length > 0 && <ul className="mt-3 space-y-1 text-xs">{m.risks.map((r) => <li key={r} className="flex gap-1.5"><AlertTriangle className="h-3.5 w-3.5 text-amber-500 shrink-0" />{r}</li>)}</ul>}
  </div>
);

const TrackAllocation = ({ f, setF, reload }) => {
  const [busy, setBusy] = useState(false);
  const [eid, setEid] = useState("");
  const ents = useLoad(() => api.get("/entities").then((r) => r.data));
  if (f.allocation_id) return <p className="text-sm" data-testid="deal-allocation-linked"><Rocket className="inline h-4 w-4 text-primary mr-1" />Tracked as an allocation — <Link to="/app/growth" className="text-primary hover:underline">see results in Growth & Allocation</Link></p>;
  const run = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/deals/${f.id}/to-allocation`, { entity_id: eid || null });
      setF({ ...f, allocation_id: r.data.id });
      reload();
      toast.success("Now tracked as an allocation, with reviews at 90, 180 and 365 days");
    } catch (e) {
      toast.error(errMsg(e));
    }
    setBusy(false);
  };
  return (
    <div className="rounded-lg border border-primary/30 bg-primary/[0.04] p-3 text-sm flex flex-wrap items-center gap-3" data-testid="deal-to-allocation">
      <span className="flex-1 min-w-[220px]">Deal closed. Track it as an allocation so AccruallQ compares actual results with the numbers you underwrote.</span>
      {ents.data?.length > 0 && <div className="w-56"><SimpleSelect value={eid} onChange={setEid} options={ents.data.map((e) => ({ value: e.id, label: e.name }))} allowNone noneLabel="Which company owns it?" testId="deal-to-allocation-entity" /></div>}
      <Button size="sm" onClick={run} disabled={busy} data-testid="deal-to-allocation-btn">{busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Rocket className="h-4 w-4 mr-1.5" />}Track as allocation</Button>
    </div>
  );
};

const DealDialog = ({ deal, stages, onClose, reload }) => {
  const [f, setF] = useState(deal);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef();
  const tasks = useLoad(() => (f.id ? api.get(`/tasks?deal_id=${f.id}`).then((r) => r.data) : Promise.resolve([])), [f.id]);
  const set = (k) => (v) => setF({ ...f, [k]: v?.target ? v.target.value : v });
  const save = async () => {
    setBusy(true);
    try {
      const body = { ...blank, ...f, next_action_date: f.next_action_date || null };
      const r = f.id ? await api.put(`/deals/${f.id}`, body) : await api.post("/deals", body);
      reload();
      if (f.id) { onClose(); } else { setF(r.data); toast.success("Deal added — upload a teaser or financials to extract metrics"); }
    } catch (e) {
      toast.error(errMsg(e));
    }
    setBusy(false);
  };
  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post(`/deals/${f.id}/files`, fd);
      setF(r.data);
      reload();
      toast.success("Document analyzed");
    } catch (e) {
      toast.error(errMsg(e));
    }
    setBusy(false);
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="deal-dialog">
        <DialogHeader><DialogTitle>{f.id ? f.name : "New deal"}</DialogTitle></DialogHeader>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Business name"><Input value={f.name} onChange={set("name")} data-testid="deal-name-input" /></Field>
          <Field label="Stage"><SimpleSelect value={f.stage} onChange={set("stage")} options={listOptions(stages)} testId="deal-stage-select" /></Field>
          <Field label="Sector"><Input value={f.sector} onChange={set("sector")} data-testid="deal-sector-input" /></Field>
          <Field label="Location"><Input value={f.location} onChange={set("location")} data-testid="deal-location-input" /></Field>
          <Field label="Source (broker, referral…)"><Input value={f.source} onChange={set("source")} data-testid="deal-source-input" /></Field>
          <Field label="Next action date"><Input type="date" value={f.next_action_date || ""} onChange={set("next_action_date")} data-testid="deal-next-date-input" /></Field>
          <div className="sm:col-span-2"><Field label="Next action"><Input value={f.next_action} onChange={set("next_action")} placeholder="e.g. Request 3 years of tax returns" data-testid="deal-next-action-input" /></Field></div>
          <div className="sm:col-span-2"><Field label="Notes"><textarea rows={3} value={f.notes} onChange={set("notes")} className="w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm" data-testid="deal-notes-input" /></Field></div>
        </div>
        <div className="flex gap-2"><Button onClick={save} disabled={busy || !f.name} data-testid="deal-save-btn">{busy && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />}{f.id ? "Save" : "Create deal"}</Button></div>
        {f.id && (
          <>
            <div className="flex flex-wrap items-center gap-2 mt-2">
              <input ref={fileRef} type="file" hidden accept=".pdf,.txt,image/*" onChange={(e) => upload(e.target.files[0])} data-testid="deal-file-input" />
              <Button variant="outline" onClick={() => fileRef.current.click()} disabled={busy} data-testid="deal-upload-btn"><Upload className="h-4 w-4 mr-1.5" />Upload teaser, CIM or financials</Button>
              {(f.files || []).map((x) => <button key={x.id} onClick={() => openFile(`/deals/${f.id}/files/${x.id}`)} className="text-xs inline-flex items-center gap-1 text-primary hover:underline" data-testid={`deal-file-${x.id}`}><FileText className="h-3.5 w-3.5" />{x.filename}</button>)}
            </div>
            {deal.stage === "Closed" && <TrackAllocation f={f} setF={setF} reload={reload} />}
            {Object.keys(f.metrics || {}).length > 0 && <Metrics m={f.metrics} />}
            <ResearchPanel path={`/deals/${f.id}/research`} initial={f.research} label="Research this company" feature="deals" onDone={() => reload()} />
            <PlaybookCheck situation={`Considering acquiring ${f.name} (${f.stage}, ${f.sector || "sector n/a"}). ${f.notes || ""}`} category="acquisition" dealId={f.id} label="Check this deal against my playbook" />
            <div className="mt-2"><div className="font-semibold text-sm mb-2">Follow-ups for this deal</div>
              <TaskForm ents={[]} dealId={f.id} onSaved={tasks.reload} />
              <TaskList tasks={tasks.data || []} reload={tasks.reload} canEdit /></div>
            {f.history?.length > 0 && <div className="text-xs text-muted-foreground mt-2">History: {f.history.map((h) => `${fmtDate(h.at)} ${h.event}`).join(" · ")}</div>}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

const Compare = ({ advanced }) => {
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try {
      setRes((await api.post("/deals/compare")).data);
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || errMsg(e));
    }
    setBusy(false);
  };
  return (
    <>
      <Button variant="outline" onClick={run} disabled={busy} data-testid="deals-compare-btn">{busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Scale className="h-4 w-4 mr-1.5" />}Compare & prioritize{!advanced && " · Operator"}</Button>
      {res && (
        <Dialog open onOpenChange={() => setRes(null)}>
          <DialogContent className="max-w-2xl" data-testid="deals-compare-dialog">
            <DialogHeader><DialogTitle>Where to focus</DialogTitle></DialogHeader>
            <p className="text-sm">{res.summary}</p>
            <ol className="space-y-2 mt-2">{res.ranking.map((r, i) => (
              <li key={i} className="rounded-lg border border-border p-3 text-sm"><div className="flex justify-between gap-2"><b>{r.rank}. {r.name}</b>
                <span className={`text-xs font-semibold uppercase ${r.verdict === "pursue" ? "text-emerald-500" : r.verdict === "pass" ? "text-red-500" : "text-amber-500"}`}>{r.verdict}</span></div>
                <div className="text-muted-foreground mt-1">{r.why}</div><div className="text-xs mt-1">Next: {r.next_step}</div></li>))}</ol>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
};

export default function Deals() {
  const { user } = useAuth();
  const state = useLoad(() => api.get("/deals").then((r) => r.data));
  const [open, setOpen] = useState(null);
  const d = state.data;
  const canEdit = user?.workspace?.role !== "viewer";
  return (
    <div>
      <PageHeader eyebrow="Acquisitions" title="Deals pipeline" subtitle="Track every opportunity from first look to close. Upload a teaser or financials and Claude extracts the numbers and risks.">
        {d && <Compare advanced={d.advanced} />}
        {d && canEdit && <Button onClick={() => setOpen(blank)} data-testid="deal-new-btn"><Plus className="h-4 w-4 mr-1.5" />New deal</Button>}
      </PageHeader>
      {state.gated && (
        <div className="surface p-5 mb-6 opacity-90" data-testid="deals-sample">
          <div className="text-xs font-semibold text-muted-foreground mb-3">EXAMPLE PIPELINE</div>
          <div className="grid gap-3 sm:grid-cols-3">
            {[["Reviewing", "Maple HVAC Ltd.", "Rev 3.2M · EBITDA 640K · 3.8x"], ["LOI", "Northshore Bakery", "Rev 1.1M · SDE 290K · 2.9x"], ["Due Diligence", "Clearline Signs", "Rev 2.4M · EBITDA 410K · 4.1x"]].map(([s, n, m]) => (
              <div key={n} className="rounded-xl border border-border p-3"><div className="text-[11px] uppercase text-muted-foreground">{s}</div><div className="font-semibold text-sm mt-1">{n}</div><div className="text-xs font-mono text-muted-foreground mt-1">{m}</div></div>))}
          </div>
        </div>
      )}
      <PageState state={state}>
        {d && (d.deals.length === 0 ? <Empty icon={Briefcase} title="No deals yet" text="Add a business you're considering buying to start your pipeline." testId="deals-empty" /> : (
          <div className="flex gap-4 overflow-x-auto pb-4" data-testid="deals-board">
            {d.stages.map((s) => {
              const items = d.deals.filter((x) => x.stage === s);
              return (
                <div key={s} className={`w-64 shrink-0 ${items.length ? "" : "hidden sm:block"}`} data-testid={`deal-column-${s.toLowerCase().replace(/\s+/g, "-")}`}>
                  <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">{s} · {items.length}</div>
                  <div className="space-y-2.5">{items.map((x) => <DealCard key={x.id} d={x} onOpen={setOpen} />)}</div>
                </div>
              );
            })}
          </div>
        ))}
      </PageState>
      {open && <DealDialog deal={open} stages={d.stages} onClose={() => setOpen(null)} reload={state.reload} />}
    </div>
  );
}
